/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { screen } from "@stoneage/XDL/Helpers";
import { color } from "@stoneage/XDL/Utilities";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { css } from "styled-components";

const enum fields {
    welcomeText = "welcomeText",
    textStyle = "textStyle",
    showDashboardLink = "showDashboardLink",
    homePageBannerPlacement = "homePageBannerPlacement",
    colorTheme = "colorTheme",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.welcomeText]: string;
        [fields.homePageBannerPlacement]: boolean;
        [fields.textStyle]: string;
        [fields.showDashboardLink]: boolean;
        [fields.colorTheme]: "light" | "dark";
    };
    extendedStyles?: WelcomeStyles;
}

type Props = OwnProps & PropFromRedux;

export interface WelcomeStyles {
    widgetContainer?: InjectableCss;
    welcomeMessage?: TypographyProps;
    linkContainer?: InjectableCss;
    icon?: IconProps;
    page?: InjectableCss;
}

export const welcomeStyles: WelcomeStyles = {
    widgetContainer: {
        css: css`
            position: absolute;
            top: 94px;
            border-bottom: solid 1px ${color("common.white", 0.33)};
            width: 100%;
            padding-top: 24px;

            ${screen("lte", "md")`
                top: 0;
            `}

            ${({ banner }: any) => {
                if (!banner) {
                    return `
                        position: relative;
                        top: 0;
                        padding-top: 0;
                    `;
                }
            }}
        `,
    },
    page: {
        css: css`
            display: flex;
            align-items: flex-end;
            gap: 32px;
            padding-bottom: 7px;

            ${screen("lte", "xs")`
                flex-direction: column;
                align-items: flex-start;
                gap: 0px;
            `}
        `,
    },
    welcomeMessage: {
        css: css`
            margin-bottom: 0;
        `,
    },
    linkContainer: {
        css: css`
            & span {
                display: flex;
                align-items: center;
                font-size: 12px;
            }
        `,
    },
    icon: {
        src: "ChevronRight",
        size: 15,
        css: css`
            margin-left: 5px;
        `,
    },
};

const Welcome = ({ id, fields, userName, firstName, myAccountPageUrl, isAuthenticated, extendedStyles }: Props) => {
    if (!isAuthenticated) {
        return <></>;
    }
    const [styles] = React.useState(() => mergeToNew(welcomeStyles, extendedStyles));

    const getTextColor = () => (fields.colorTheme === "light" ? "common.black" : "common.white");

    return (
        <StyledWrapper
            css={css`
                position: relative;
                z-index: 2;
            `}
        >
            <StyledWrapper {...styles.widgetContainer} {...({ banner: fields.homePageBannerPlacement } as any)}>
                <StyledWrapper as="div" {...styles.page} {...({ banner: fields.homePageBannerPlacement } as any)}>
                    <StyledWrapper>
                        <Typography
                            variant={fields.textStyle as keyof ThemeTypography}
                            {...styles.welcomeMessage}
                            color={getTextColor()}
                            translate="no"
                        >
                            {fields.welcomeText} {firstName || userName}
                        </Typography>
                    </StyledWrapper>
                    {fields.showDashboardLink && (
                        <StyledWrapper>
                            <Link href={myAccountPageUrl} color={getTextColor()} {...styles.linkContainer}>
                                {translate("Visit Dashboard")}
                                <Icon color={getTextColor()} {...styles.icon} />
                            </Link>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        userName,
        firstName: state.context.session.firstName,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const connector = connect(mapStateToProps);
type PropFromRedux = ConnectedProps<typeof connector>;

const welcome: WidgetModule = {
    component: connector(Welcome),
    definition: {
        displayName: "Welcome User",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.welcomeText,
                displayName: "Welcome Text",
                editorTemplate: "TextField",
                defaultValue: "Welcome, ",
                fieldType: "Translatable",
            },
            {
                name: fields.textStyle,
                displayName: "Message Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 2", value: "h2" },
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.showDashboardLink,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.homePageBannerPlacement,
                displayName: "Place on HomePage Banner",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.colorTheme,
                editorTemplate: "DropDownField",
                defaultValue: "light",
                fieldType: "General",
                options: [
                    { displayName: "Light", value: "light" },
                    { displayName: "Dark", value: "dark" },
                ],
            },
        ],
    },
};

export default welcome;
